import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { MapPlace } from './PlaceMapView';
import { placeMarkerHtml, PlaceMarkerMode, myLocationHtml } from './markerStyle';
import './LeafletMap.css';

/**
 * 해외 장소 고르기 지도 — Leaflet + OSM.
 *
 * 국내(카카오) 경로가 하는 일을 같은 순서로 합니다.
 *   · 마커는 markerStyle.placeMarkerHtml — 카카오와 같은 HTML
 *   · 지도가 멈추면 화면 중심을 올려보내고, '여기서 검색' 은 PlaceMapView 가 그립니다
 *   · 지도를 누르면 좌표를 올려보내 임의 장소를 만듭니다
 *   · 고른 장소는 상세 패널에 가리지 않게 위로 올려 panTo 합니다
 *
 * 구글 지도를 쓰지 않는 이유 — 웹뷰 안에서 Maps JavaScript API 를 띄우면
 * 지도 로드마다 과금됩니다. 앱에서는 네이티브 지도(react-native-maps)로
 * 넘기고, 브라우저에서는 무료인 OSM 타일을 씁니다.
 */

interface Props {
    places: MapPlace[];
    center: { lat: number; lng: number };
    selectedPlaceId?: string | null;
    onPlaceSelect?: (place: MapPlace) => void;
    /** 지도를 누른 좌표 — 부모가 임의 장소를 만듭니다 */
    onMapClick?: (lat: number, lng: number) => void;
    /** 지도가 멈출 때마다의 화면 중심. '여기서 검색' 판단은 부모가 합니다. */
    onIdleCenter?: (center: { lat: number; lng: number }) => void;
    /** 상세 패널이 열려 있으면 고른 장소를 더 위로 올립니다 (카카오와 같은 규칙) */
    detailOpen?: boolean;
    markerMode?: PlaceMarkerMode;
    /** 내 위치 — 점으로 찍습니다. 국내(카카오)와 같은 모양을 씁니다 */
    myLocation?: { lat: number; lng: number } | null;
}

/* 카카오 level(작을수록 확대, 1~14) ↔ Leaflet zoom(클수록 확대).
   카카오 기본 level 4 = zoom 15. */
const toLeafletZoom = (level: number) => Math.max(3, Math.min(19, 19 - level));
const toKakaoLevel = (zoom: number) => 19 - zoom;

export const LeafletPlacePicker: React.FC<Props> = ({
    places,
    center,
    selectedPlaceId,
    onPlaceSelect,
    onMapClick,
    onIdleCenter,
    detailOpen,
    markerMode = 'simple',
    myLocation,
}) => {
    const boxRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    /** 지도가 만들어졌는가 — ref 만으로는 효과를 다시 돌릴 수 없습니다 */
    const [mapReady, setMapReady] = useState(false);
    const markersRef = useRef<L.Marker[]>([]);

    /* 핸들러는 ref 로 들고 있습니다. 지도 리스너는 처음 한 번만 달고,
       그 안에서 늘 최신 핸들러를 부르게 하려는 것입니다. */
    const onPlaceSelectRef = useRef(onPlaceSelect);
    onPlaceSelectRef.current = onPlaceSelect;
    const onMapClickRef = useRef(onMapClick);
    onMapClickRef.current = onMapClick;
    const onIdleCenterRef = useRef(onIdleCenter);
    onIdleCenterRef.current = onIdleCenter;

    // ── 지도 만들기 (한 번만)
    useEffect(() => {
        if (!boxRef.current || mapRef.current) return;
        const map = L.map(boxRef.current, {
            attributionControl: false,
            zoomControl: false,   // 카카오 장소 고르기에도 줌 컨트롤이 없습니다
        }).setView([center.lat, center.lng], toLeafletZoom(4));

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
        L.control.attribution({ position: 'bottomleft', prefix: false })
            .addAttribution('© OpenStreetMap')
            .addTo(map);

        // 화면이 멈추면 중심을 올려보냅니다 — 카카오의 idle 리스너와 같은 자리
        map.on('moveend', () => {
            const c = map.getCenter();
            onIdleCenterRef.current?.({ lat: c.lat, lng: c.lng });
        });

        // 지도를 누르면 좌표를 올려보냅니다 — 부모가 임의 장소를 만듭니다
        map.on('click', (e: L.LeafletMouseEvent) => {
            onMapClickRef.current?.(e.latlng.lat, e.latlng.lng);
        });

        mapRef.current = map;
        setMapReady(true);
        return () => { map.remove(); mapRef.current = null; setMapReady(false); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* places 는 부모가 매 렌더 새로 만드는 배열입니다. 그대로 deps 에 넣으면
       렌더마다 마커를 지우고 다시 그려 깜빡입니다. 국내(카카오) 경로가 쓰는
       placesKey 와 같은 방식으로 실제로 목록이 바뀔 때만 다시 그립니다. */
    const placesKey = places.map(p => p.id).join(',');
    const placesRef = useRef(places);
    placesRef.current = places;

    // ── 마커 — 카카오와 같은 HTML, 같은 클릭 동작
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        markersRef.current.forEach(m => m.remove());
        markersRef.current = [];

        placesRef.current.forEach(place => {
            if (!place.lat || !place.lng) return;
            const isSelected = selectedPlaceId === place.id;
            /* 카카오 CustomOverlay 의 xAnchor/yAnchor 0.5 와 같은 자리에 두기 위해
               감싼 div 를 translate(-50%,-50%) 합니다. 마커 크기가 내용에 따라
               달라지므로 iconSize 로는 가운데를 맞출 수 없습니다. */
            const icon = L.divIcon({
                html: `<div style="transform:translate(-50%,-50%);display:inline-block;">${placeMarkerHtml(place, isSelected, markerMode)}</div>`,
                className: '',       // Leaflet 기본 흰 네모 배경 제거
                iconSize: undefined,
                iconAnchor: [0, 0],
            });
            const marker = L.marker([place.lat, place.lng], {
                icon,
                zIndexOffset: isSelected ? 1000 : 0,
                title: place.name || '',
            }).addTo(map);
            marker.on('click', (e: L.LeafletMouseEvent) => {
                L.DomEvent.stopPropagation(e);
                onPlaceSelectRef.current?.(place);
                map.setView([place.lat, place.lng], map.getZoom());
            });
            markersRef.current.push(marker);
        });

        return () => {
            markersRef.current.forEach(m => m.remove());
            markersRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placesKey, selectedPlaceId, markerMode]);

    /* 내 위치 점 — 장소 마커와 따로 둡니다. 같이 두면 목록이 바뀔 때마다
       지워졌다 다시 그려져 깜빡입니다. */
    const myLocMarkerRef = useRef<L.Marker | null>(null);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        if (myLocMarkerRef.current) {
            myLocMarkerRef.current.remove();
            myLocMarkerRef.current = null;
        }
        if (!myLocation) return;
        myLocMarkerRef.current = L.marker([myLocation.lat, myLocation.lng], {
            icon: L.divIcon({
                className: '',
                html: `<div style="transform:translate(-50%,-50%);display:inline-block;">${myLocationHtml()}</div>`,
                iconSize: [0, 0],
            }),
            interactive: false,   // 점 때문에 지도를 못 누르면 안 됩니다
            zIndexOffset: -100,   // 장소 마커보다 아래
        }).addTo(map);
        return () => {
            if (myLocMarkerRef.current) {
                myLocMarkerRef.current.remove();
                myLocMarkerRef.current = null;
            }
        };
        // 좌표가 지도보다 먼저 올 수 있어 mapReady 도 봅니다
    }, [myLocation, mapReady]);

    // ── 고른 장소를 상세 패널 위 중앙에 — 카카오와 같은 계산
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !selectedPlaceId) return;
        const found = placesRef.current.find(p => p.id === selectedPlaceId);
        if (!found) return;
        const level = toKakaoLevel(map.getZoom());
        const baseOffset = Math.pow(2, level - 1) * 0.0006;
        const latOffset = detailOpen ? baseOffset : baseOffset * 0.2;
        map.panTo([found.lat - latOffset, found.lng]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPlaceId, detailOpen]);

    // ── 바깥에서 center 가 바뀌면 따라갑니다 (즐겨찾기 · 검색 결과 선택 등)
    useEffect(() => {
        if (!mapRef.current || !center) return;
        mapRef.current.setView([center.lat, center.lng], mapRef.current.getZoom());
    }, [center]);

    return <div ref={boxRef} style={{ width: '100%', height: '100%' }} />;
};
